import { getAuth, sendSignInLinkToEmail } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { logDebug, logError } from "../../shared/common/logger";
import { getEstimateStore, getUserStore } from "../../data/DataStores";
import RestAPI from "../../utils/restAPI";
import SharedUtils from "../../shared/common/SharedUtils";
import { LassoSimpleButton, LassoSimpleButtonFull } from "../../components/ui/utils/LassoButtons";
import bgImage from "./assets/estimatices/bg.jpg";
import LoadingAnimation from "../../components/ui/utils/LoadingAnimation";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "../../components/ui/utils/InputOTP";
enum Instructions {
	NONE = 0,
	ALLOWED = 1,
	DISALLOWED = 2,
}

const LassoMVPLoginPage: React.FC = () => {
	const [buttonEnabled, setButtonEnabled] = useState(false);
	const [email, setEmail] = useState("");
	const userStore = getUserStore();
	const [showInstructions, setShowInstructions] = useState<Instructions>(Instructions.NONE);
	const [fetching, setFetching] = useState(false);
	const [showError, setShowError] = useState(false);
	const [code, setCode] = useState("");
	const [codeError, setCodeError] = useState("");
	const [verifying, setVerifying] = useState(false);

	const showExpiredError = location.search.includes("expired-token");

	const onLogin = async () => {
		try {
			setFetching(true);
			try {
				window.localStorage.setItem("passwordless_email", email);
				const data = await RestAPI.fetchAPI(
					"/rest/access/sendSignInLink",
					"GET",
					{
						origin: location.origin, // TODO :: check why tsed some time not able to get origin
						email: email,
					},
					"data",
					false
				);
				logDebug("data", data);
				setShowInstructions(data?.email ? Instructions.ALLOWED : Instructions.DISALLOWED);
			} catch (err) {
				setShowError(true);
			}
			setFetching(false);
		} catch (err) {
			logError("LassoMVPLandingPage", err);
		}
	};

	const onVerifyCode = async () => {
		if (code.length !== 6) return;

		setVerifying(true);
		setCodeError("");

		try {
			const result = await RestAPI.fetchAPI(
				"/rest/access/checkCode",
				"GET",
				{
					code,
					email,
				},
				"",
				false
			);

			if (result?.success) {
				const link = result.data;
				if (link) {
					window.location.href = link;
				}
			} else {
				setCodeError(result?.error || "Invalid code. Please try again.");
				setVerifying(false);
			}
		} catch (err) {
			logError("Error verifying code", err);
			setCodeError("An error occurred. Please try again.");
			setVerifying(false);
		}
	};

	useEffect(() => {
		if (email && SharedUtils.testEmail(email)) {
			setButtonEnabled(true);
		} else {
			setButtonEnabled(false);
		}
	}, [email]);

	useEffect(() => {
		if (code.length === 6 && !verifying) {
			onVerifyCode();
		}
	}, [code]);

	const registerNewUser = async () => {
		const org = await RestAPI.fetchAPI("/rest/access/registerNewUser", "POST", {
			newUser: { email: userStore.user?.email, name: "Rebuild Admin" },
		});

		if (org) {
			location.href = "/mvp/test";
		}
	};

	const checkUserRegistered = async () => {
		const registered = await RestAPI.fetchAPI("/rest/access/isMyUserRegistered");
		if (registered) {
			location.href = "/mvp/test";
		} else {
			// show register flow
		}
	};
	useEffect(() => {
		if (userStore.user) {
			// check if register and redirect or show register form
			checkUserRegistered();
		}
	}, [userStore.user]);

	if (userStore.haveServerIssues) {
		return (
			<div
				className="flex items-center justify-center h-[100vh]"
				style={{
					backgroundImage: "url(/assets/estimatics/bg.jpg)",
					backgroundSize: "cover",
					backgroundPosition: "center",
				}}
			>
				<div className="w-[800px] h-[230px] pt-14 pb-20 bg-white rounded-xl shadow flex-col justify-start items-center gap-8 inline-flex">
					<div className="self-stretch h-[94px] px-12 bg-white flex-col justify-start items-center gap-6 flex">
						<div className="self-stretch h-[94px] flex-col justify-start items-center gap-4 flex">
							<div className="self-stretch text-center text-gray-900 text-[32px] font-semibold font-['Hanken Grotesk'] leading-[38.40px]">
								We are currently having server issues
							</div>
							<div className="self-stretch text-center text-slate-600 text-sm font-normal font-['Inter'] leading-tight">
								We are sorry for the inconvenience. Our team is working on resolving the issue now.
								<br />
								<br />
								Please try again later.
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (showExpiredError) {
		return (
			<div
				className="flex items-center justify-center h-[100vh]"
				style={{
					backgroundImage: "url(/assets/estimatics/bg.jpg)",
					backgroundSize: "cover",
					backgroundPosition: "center",
				}}
			>
				<div className="w-[800px] h-[464px] pt-14 pb-20 bg-white rounded-xl shadow flex-col justify-start items-center gap-8 inline-flex">
					<div className="w-[88px] h-[88px] justify-center items-center inline-flex">
						<div className="grow shrink basis-0 self-stretch pl-[11.14px] pr-[10.86px] py-[11px] justify-center items-center inline-flex relative">
							<div className="w-[77px] h-[77px] bg-amber-50 rounded-full absolute" />
							<img
								src="/assets/ui/broken.svg"
								className="w-[66px] h-[66px] relative flex-col justify-start items-start flex"
							/>
						</div>
					</div>
					<div className="self-stretch h-[132px] px-12 bg-white flex-col justify-start items-center gap-6 flex">
						<div className="self-stretch h-[132px] flex-col justify-start items-center gap-4 flex">
							<div className="self-stretch text-center text-gray-900 text-[32px] font-semibold font-['Hanken Grotesk'] leading-[38.40px]">
								Login Link Expired
							</div>
							<div className="self-stretch text-center">
								<span className="text-slate-600 text-sm font-normal font-['Hanken Grotesk'] leading-tight no-break">
									The link you clicked on has expired. For security reasons, our links are only valid for a limited time.
									<br />
									<br />
									Please try logging in again.
								</span>
								<span className="text-blue-900 text-sm font-normal font-['Inter'] underline leading-tight"></span>
							</div>
						</div>
					</div>
					<div className="w-[800px] px-12 justify-center items-start inline-flex">
						<div
							className="btn w-[132px] px-[18px] py-2.5 bg-blue-900 rounded-lg shadow border border-blue-900 justify-center items-center gap-2 flex"
							onClick={() => {
								location.href = "/";
							}}
						>
							<div className="no-break text-white text-base font-semibold font-['Hanken Grotesk'] leading-normal">
								Try again
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	} else if (showInstructions === Instructions.ALLOWED) {
		return (
			<div
				className="flex items-center justify-center h-[100vh]"
				style={{
					backgroundImage: "url(/assets/estimatics/bg.jpg)",
					backgroundSize: "cover",
					backgroundPosition: "center",
				}}
			>
				<div className="w-[800px] pt-14 pb-12 bg-white rounded-xl shadow flex-col justify-start items-center gap-8 inline-flex">
					<div className="self-stretch px-12 bg-white flex-col justify-start items-center gap-6 flex">
						<div className="self-stretch flex-col justify-start items-center gap-4 flex">
							<div className="self-stretch text-center text-gray-900 text-[32px] font-semibold font-['Hanken Grotesk'] leading-[38.40px]">
								Check your email
							</div>
							<div className="self-stretch text-center text-slate-600 text-sm font-normal font-['Inter'] leading-tight">
								We sent a 6-digit code to <span className="font-semibold">{email}</span>
							</div>
						</div>
					</div>

					<div className="px-12 flex-col justify-start items-center gap-6 flex">
						<div className="flex flex-col items-center gap-4">
							<InputOTP
								maxLength={6}
								value={code}
								onChange={(value) => {
									setCode(value);
									setCodeError("");
								}}
							>
								<InputOTPGroup>
									<InputOTPSlot index={0} />
									<InputOTPSlot index={1} />
									<InputOTPSlot index={2} />
								</InputOTPGroup>
								<InputOTPSeparator />
								<InputOTPGroup>
									<InputOTPSlot index={3} />
									<InputOTPSlot index={4} />
									<InputOTPSlot index={5} />
								</InputOTPGroup>
							</InputOTP>
							{codeError && (
								<div className="text-center text-red-600 text-sm font-normal">{codeError}</div>
							)}
						</div>

						<div className="w-full flex justify-center">
							<div
								className={`btn px-[18px] py-2.5 rounded-lg shadow border justify-center items-center gap-2 flex ${
									code.length !== 6 || verifying
										? "bg-gray-300 border-gray-300 cursor-not-allowed"
										: "bg-blue-900 border-blue-900 cursor-pointer"
								}`}
								onClick={code.length === 6 && !verifying ? onVerifyCode : undefined}
							>
								<div className="text-white text-base font-semibold font-['Hanken Grotesk'] leading-normal">
									{verifying ? "Verifying..." : "Verify Code"}
								</div>
							</div>
						</div>
					</div>

					<div className="text-center text-xs text-slate-600">
						Didn't receive a code?{" "}
						<span
							className="text-blue-900 underline cursor-pointer"
							onClick={() => {
								location.reload();
							}}
						>
							Try again
						</span>
					</div>
				</div>
			</div>
		);
	} else if (showInstructions === Instructions.DISALLOWED) {
		return (
			<div
				className="flex items-center justify-center h-[100vh]"
				style={{
					backgroundImage: "url(/assets/estimatics/bg.jpg)",
					backgroundSize: "cover",
					backgroundPosition: "center",
				}}
			>
				<div className="w-[800px] h-[464px] pt-14 pb-20 bg-white rounded-xl shadow flex-col justify-start items-center gap-8 inline-flex">
					<div className="w-[88px] h-[88px] justify-center items-center inline-flex">
						<div className="grow shrink basis-0 self-stretch pl-[11.14px] pr-[10.86px] py-[11px] justify-center items-center inline-flex">
							<img
								src="/assets/ui/no-user.svg"
								className="w-[66px] h-[66px] relative flex-col justify-start items-start flex"
							/>
						</div>
					</div>
					<div className="self-stretch h-[132px] px-12 bg-white flex-col justify-start items-center gap-6 flex">
						<div className="self-stretch h-[132px] flex-col justify-start items-center gap-4 flex">
							<div className="self-stretch text-center text-gray-900 text-[32px] font-semibold font-['Hanken Grotesk'] leading-[38.40px]">
								We could not find an account for
								<br />
								that email address
							</div>
							<div className="self-stretch text-center">
								<span className="text-slate-600 text-sm font-normal font-['Hanken Grotesk'] leading-tight no-break">
									if you are unable to log-in please reach out to your Rebuild administrator or the Rebuild support team
									at
								</span>
								<br />
								<span className="text-blue-900 text-sm font-normal font-['Inter'] underline leading-tight">
									admin@goLasso.ai
								</span>
							</div>
						</div>
					</div>
					<div className="w-[800px] px-12 justify-center items-start inline-flex">
						<div
							className="btn w-[132px] px-[18px] py-2.5 bg-blue-900 rounded-lg shadow border border-blue-900 justify-center items-center gap-2 flex"
							onClick={() => {
								location.reload();
							}}
						>
							<div className="no-break text-white text-base font-semibold font-['Hanken Grotesk'] leading-normal">
								Try again
							</div>
						</div>
					</div>
				</div>
				{/* <div className="w-[800px] h-[404px] pt-14 pb-20 bg-white rounded-xl shadow flex-col justify-start items-center gap-8 inline-flex">
				<img className="w-[66px] h-[66px]" src="/assets/ui/passwordless-error.png" />
				<div className="self-stretch h-[94px] px-12 bg-white flex-col justify-start items-center gap-6 flex">
					<div className="self-stretch h-[94px] flex-col justify-start items-center gap-4 flex">
						<div className="self-stretch text-center text-gray-900 text-[32px] font-semibold font-['Hanken Grotesk'] leading-[38.40px]">oh snap </div>
						<div className="self-stretch text-center text-slate-600 text-sm font-normal font-['Inter'] leading-tight">email is not allowed please contact admin .</div>
					</div>
				</div>
				<div className="w-[800px] px-12 justify-center items-start inline-flex">
					<div className="w-[108px] px-[18px] py-2.5 bg-blue-900 rounded-lg shadow border border-blue-900 justify-center items-center gap-2 flex">
						<LassoSimpleButtonFull caption='Try Again' onClick={() => {
							location.reload();
						}}></LassoSimpleButtonFull>
					</div>
				</div>
			</div> */}
			</div>
		);
	}
	return (
		<div
			className="flex items-center justify-center h-[100vh]"
			style={{
				backgroundImage: "url(/assets/estimatics/bg.jpg)",
				backgroundSize: "cover",
				backgroundPosition: "center",
			}}
		>
			<div className="w-[800px] h-[306px] pt-14 pb-20 bg-white rounded-xl shadow flex-col justify-start items-center gap-8 inline-flex">
				<div className="self-stretch h-[94px] px-12 bg-white flex-col justify-start items-center gap-6 flex">
					<div className="self-stretch h-[94px] flex-col justify-start items-center gap-4 flex  select-none">
						<div className="self-stretch text-center text-gray-900 text-[32px] font-semibold font-['Hanken Grotesk'] leading-[38.40px]">
							Welcome to Rebuild Estimates Portal
						</div>
						<div className="self-stretch text-center text-slate-600 text-sm font-normal font-['Inter'] leading-tight">
							Simplify your estimating process with Rebuild. To access your account, enter your email below
						</div>
					</div>
				</div>
				<div className="w-[800px] px-12 justify-start items-start inline-flex">
					<div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
						<div className="self-stretch h-11 flex-col justify-start items-start gap-1.5 flex">
							<div className="self-stretch px-3.5 py-2.5 bg-white rounded-tl-lg rounded-bl-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex">
								<div className="justify-start items-center gap-2 flex">
									<input
										onKeyDown={(e) => {
											if (e.key === "Enter" && buttonEnabled) {
												onLogin();
											}
										}}
										type="text"
										className="w-[500px] border-0 focus:outline-none"
										placeholder="Email Address"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
									/>
								</div>
							</div>
						</div>
					</div>
					<LassoSimpleButton disabled={!buttonEnabled} onClick={onLogin} caption="Send Login Link" />
				</div>
			</div>
			{fetching && <LoadingAnimation />}
		</div>
	);
};
export default LassoMVPLoginPage;
